import {hasFlag, isNullOrEmpty, translation, UserError} from "@2d-front-monorepo/core";
import {MvvmDataProviderRef, showErrorDialog} from "@2d-front-monorepo/design-library";

import {
	Address, Addresses,
	CreateOrSelectResidencyComponent,
	CreateOrSelectResidencyModel, EntityProfile,
	IntegraZone,
	ResidencyPropertyFlags,
	ResidencyResult,
	ResidencyUses,
	ResidencyWorkflowOptions,
	ZoneId, Zones
} from "@2d-front-monorepo/permits-business";

import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, memo, useCallback, useMemo, useRef, useState} from "react";
import {API_ENDPOINT} from "../../api-endpoint";
import BoundedGeoapifyAddressComponent from "../../components/BoundedGeoapifyAddressComponent";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import {getHttpClient} from "../../hooks/getHttpClient";

const MemoizedAddressComponent = memo(BoundedGeoapifyAddressComponent);

export type ResidencyBaseProps = {
	initialState: CreateOrSelectResidencyModel;
	initialZones: IntegraZone[];
	residencyUses: ResidencyUses;
	configuration: EntityProfile<ResidencyPropertyFlags>;
	residencies: ResidencyResult[];
	createEnabled: boolean;
	onSubmit: (result: CreateOrSelectResidencyModel) => Promise<void>;
	footer: (state: CreateOrSelectResidencyModel) => JSX.Element;
	zoneIdWarning?: ZoneId;
	onZonesChanged?: (zones: IntegraZone[]) => void;
	options: ResidencyWorkflowOptions;
	onBeforeSubmit?: (result: CreateOrSelectResidencyModel) => Promise<void>;
}

type LookupRef = {
	lastQuery?: { street: string; streetNumber: string };
	inflight: boolean;
	pending?: { street: string; streetNumber: string };
};

export default function ResidencyBase(props: ResidencyBaseProps): JSX.Element {
	const {
		initialState, initialZones, residencyUses, configuration, residencies,
		createEnabled, onSubmit, footer, zoneIdWarning, onZonesChanged,
		options, onBeforeSubmit
	} = props;

	const useAutocomplete = hasFlag(options, ResidencyWorkflowOptions.UseAutocomplete);
	const useRemoteZones = hasFlag(options, ResidencyWorkflowOptions.UseRemoteZones);

	const globalContext = getGlobalContext();
	const {installation} = globalContext;
	const addressData = installation.configuration.addressData;
	const httpClient = getHttpClient();

	const contextRef = useRef<MvvmDataProviderRef<CreateOrSelectResidencyModel>>(undefined!);
	const [hasMinimalData, setHasMinimalData] = useState<boolean>(
		() => !useAutocomplete || Addresses.hasMinimalData(initialState.arguments.address)
	);
	const [innerZones, setInnerZones] = useState<IntegraZone[]>(() => initialZones);
	const lookupRef = useRef<LookupRef>({inflight: false});

	const runRemoteZonesLookup = useCallback(async function (street: string, streetNumber: string): Promise<void> {
		const last = lookupRef.current.lastQuery;
		if (last && last.street === street && last.streetNumber === streetNumber) {
			return;
		}

		if (lookupRef.current.inflight) {
			lookupRef.current.pending = {street, streetNumber};
			return;
		}

		lookupRef.current.inflight = true;

		const previousZoneId = contextRef.current?.context.state.arguments.zoneId;
		contextRef.current?.context.update(state => {
			state.arguments.zoneId = ("" as ZoneId);
		});

		setInnerZones([]);
		onZonesChanged?.([]);

		try {
			const remoteZones = await httpClient.global.addresses({
				exactMatch: false,
				street,
				streetNumber
			});

			if (remoteZones.length === 0) {
				showErrorDialog(new UserError(
					{text: translation(PermitTranslations.new_permit_page__find_address__missing)},
					{text: translation(PermitTranslations.new_permit_page__find_address__missing_body)}
				));
				return;
			}

			const filteredZones = remoteZones.filter(x => (x.uses & residencyUses) === residencyUses && x.zoneId != null);
			if (filteredZones.length === 0) {
				showErrorDialog(new UserError(
					{text: translation(PermitTranslations.new_permit_page__find_address__invalid)},
					{text: translation(PermitTranslations.new_permit_page__find_address__invalid_body)}
				));
				return;
			}

			const firstAddress = filteredZones[0].address;
			const pcByZone = firstAddress.postalCode;
			const cityByZone = firstAddress.city;
			const newZones = filteredZones.map<IntegraZone>(x => Zones.get(installation, x.zoneId));

			contextRef.current?.context.update(state => {
				const targetAddress = state.arguments.address;
				if (!isNullOrEmpty(pcByZone)) {
					targetAddress.postalCode = pcByZone;
				}
				if (!isNullOrEmpty(cityByZone)) {
					targetAddress.city = cityByZone;
				}
				if (previousZoneId != null && newZones.find(z => z.id === previousZoneId) != null) {
					state.arguments.zoneId = previousZoneId;
				} else {
					state.arguments.zoneId = ("" as ZoneId);
				}
			});

			setInnerZones(newZones);
			setHasMinimalData(true);
			onZonesChanged?.(newZones);
			lookupRef.current.lastQuery = {street, streetNumber};

		} catch (error) {
			showErrorDialog(error);

		} finally {
			lookupRef.current.inflight = false;
			const pending = lookupRef.current.pending;

			if (pending != null && (pending.street !== street || pending.streetNumber !== streetNumber)) {
				lookupRef.current.pending = undefined;
				void runRemoteZonesLookup(pending.street, pending.streetNumber);

			} else {
				lookupRef.current.pending = undefined;
			}
		}
	}, [installation, residencyUses, onZonesChanged]);

	const onAutocompleteSelect = useCallback(async function (item: Address): Promise<void> {
		contextRef.current?.context.update(state => {
			state.arguments.address = item;
		});

		setHasMinimalData(true);

		if (useRemoteZones && !isNullOrEmpty(item.street) && !isNullOrEmpty(item.streetNumber)) {
			await runRemoteZonesLookup(item.street!, item.streetNumber!);
		}
	}, [useRemoteZones, runRemoteZonesLookup]);

	const onAddressBlur = useCallback(function (address: Address): void {
		void runRemoteZonesLookup(address.street!, address.streetNumber!);
	}, [runRemoteZonesLookup]);

	const internalOnSubmit = useCallback(async function (result: CreateOrSelectResidencyModel): Promise<void> {
		if (result.residencyId === "create" && !Addresses.hasMinimalData(result.arguments.address)) {
			return;
		}

		if (onBeforeSubmit != null) {
			await onBeforeSubmit(result);
		}

		await onSubmit(result);
	}, [onBeforeSubmit, onSubmit]);

	const createParts = useMemo(() => {
		if (!useAutocomplete) return undefined;
		return {
			header: () => <MemoizedAddressComponent setAddress={onAutocompleteSelect}/>
		};
	}, [useAutocomplete, onAutocompleteSelect]);

	const hideBody = useAutocomplete && !hasMinimalData;
	const blurHandler = !useAutocomplete && useRemoteZones ? onAddressBlur : undefined;

	return (
		<CreateOrSelectResidencyComponent initialState={initialState}
		                                  onSubmit={internalOnSubmit}
		                                  contextRef={contextRef}
		                                  installation={installation}
		                                  zones={innerZones}
		                                  provinces={addressData.provinces}
		                                  cities={addressData.cities}
		                                  defaultProvince={installation.configuration.residencies.defaultProvince}
		                                  defaultCity={installation.configuration.residencies.defaultCity}
		                                  residencies={residencies}
		                                  createEnabled={createEnabled}
		                                  configuration={configuration}
		                                  hideBody={hideBody}
		                                  zoneIdWarning={zoneIdWarning}
		                                  createParts={createParts}
		                                  onAddressBlur={blurHandler}
		                                  filesPrefix={API_ENDPOINT.fileDownload}
		                                  footer={footer}/>);
}
